import { useEffect, useState } from 'react';
import clsx from 'clsx';

interface Heading {
  id: string;
  text: string;
  level: 2 | 3;
}

interface Props {
  /** CSS selector pro container, ve kterém TOC scanuje H2/H3. */
  containerSelector?: string;
  /** Při méně než tolik H2 nadpisů se TOC neukáže (krátké kapitoly nepotřebují). */
  minHeadings?: number;
}

/**
 * Pravostranný sticky Table of Contents pro dlouhé kapitoly.
 * Skenuje rendrovaný DOM (`.prose-doc h2, h3`), generuje slugy pro id, sleduje
 * scroll polohu pro highlight. Mizí na obrazovkách užších než 1440px (custom
 * `toc:` breakpoint), nezabírá místo na laptopech, kde by se nevlezl.
 */
export default function TableOfContents({
  containerSelector = '.prose-doc',
  minHeadings = 3,
}: Props) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    // Markdown se rendruje async přes react-markdown — počkej tick, než hledáme.
    const timer = setTimeout(() => {
      const container = document.querySelector(containerSelector);
      if (!container) return;
      const els = container.querySelectorAll<HTMLElement>('h2, h3');
      const result: Heading[] = [];
      const usedIds = new Set<string>();
      els.forEach((el) => {
        if (!el.id) {
          let id = slugify(el.textContent || '');
          let suffix = 2;
          while (usedIds.has(id)) {
            id = `${slugify(el.textContent || '')}-${suffix++}`;
          }
          el.id = id;
        }
        usedIds.add(el.id);
        result.push({
          id: el.id,
          text: el.textContent || '',
          level: el.tagName === 'H2' ? 2 : 3,
        });
      });
      setHeadings(result);

      // IntersectionObserver pro highlight aktuální sekce. rootMargin posune
      // „trigger zónu" o trochu níž (kvůli sticky headeru), aby aktivace byla
      // intuitivní — section se aktivuje, když její nadpis přejde přes vrch.
      const observer = new IntersectionObserver(
        (entries) => {
          // Bereme všechny visible v zóně, vybíráme nejvýše položený.
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort(
              (a, b) =>
                a.target.getBoundingClientRect().top -
                b.target.getBoundingClientRect().top,
            );
          if (visible.length > 0) {
            setActiveId(visible[0].target.id);
          }
        },
        { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
      );
      els.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    }, 100);

    return () => clearTimeout(timer);
  }, [containerSelector]);

  if (headings.length < minHeadings) return null;

  return (
    <nav className="hidden self-start toc:sticky toc:top-24 toc:block toc:w-60 toc:shrink-0">
      <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
        Obsah
      </div>
      <ul className="space-y-1.5 border-l border-gray-200 text-sm">
        {headings.map((h) => (
          <li key={h.id} className={h.level === 3 ? 'pl-7' : 'pl-3'}>
            <a
              href={`#${h.id}`}
              onClick={(e) => smoothScrollTo(e, h.id)}
              className={clsx(
                'block -ml-px border-l-2 py-1 pl-3 transition-colors',
                activeId === h.id
                  ? 'border-teal font-semibold text-teal'
                  : 'border-transparent text-gray-500 hover:text-dark',
              )}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function smoothScrollTo(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 80;
  window.scrollTo({ top, behavior: 'smooth' });
  // Přidat hash do URL, ať se dá link sdílet.
  history.replaceState(null, '', `#${id}`);
}

// Stejná logika jako v functions/lib/id.ts, ale frontend potřebuje vlastní kopii.
function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}
